<template>
    <template v-if="props.isMobile">
        <!-- チャンネルが未登録の場合 -->
        <div v-if="props.channels.length == 0" class="px-2">
            <div
                @click="redirectToChannelCreation()"
                class="w-8 h-8 rounded-full flex items-center justify-center border border-gray-300"
            >
                <i class="fa-light fa-plus mb-[1.5px]"></i>
            </div>
        </div>

        <div
            v-else
            class="px-2"
            @click="showListAll = !showListAll"
            ref="mobileMenuRef"
        >
            <div class="w-8 h-8">
                <img
                    :src="currentChannel.channel_icon"
                    class="w-8 h-8 rounded-full"
                />
            </div>
        </div>

        <div
            v-if="showListAll && (props.channels.length > 0)"
            class="fixed top-16 left-10 z-10 rounded overflow-hidden shadow"
        >
            <ExpandedChannelList
                :channels="props.channels"
                :current-channel="currentChannel"
                @channel-selected="changeChannel"
                @close-channel-list="showListAll = false"
                @create-channel="redirectToChannelCreation()"
            />
        </div>
    </template>

    <template v-else>
        <div v-if="props.channels.length == 0">
            <div
                @click="redirectToChannelCreation()"
                class="box-border flex flex-row gap-2 items-center justify-start pl-4 pr-4 py-2 relative rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors bg-white"
            >
                <!-- アイコン部分 -->
                <div
                    class="relative shrink-0 w-8 h-8 flex items-center justify-center"
                    @click="redirectToChannelCreation()"
                >
                    <i class="fa-solid fa-plus text-main text-sm"></i>
                </div>

                <!-- テキスト部分 -->
                <div class="flex flex-col gap-0.5 items-start justify-center pr-2">
                    <div class="text-main text-sm font-semibold leading-5 whitespace-nowrap">
                        新規チャンネルを登録
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="flex flex-col" ref="menuRef">
            <div
                class="w-[210px] py-2 px-4 rounded-md outline outline-[1px] outline-gray-300 outline-offset-[-0.5px] justify-start items-center gap-2 inline-flex cursor-pointer hover:bg-muted"
                @click="showListAll = !showListAll"
            >
                <div class="w-8 h-8 relative shrink-0">
                    <div class="w-8 h-8 shrink-0 bg-white rounded-full border border-[#DFE4EC]"></div>
                    <img
                        :src="currentChannel.channel_icon"
                        class="w-8 h-8 shrink-0 absolute left-0 top-0 rounded-full"
                    />
                </div>

                <div class="flex-col justify-center items-start gap-0.5 text-left text-main min-w-0 flex-1">
                    <div class="text-sm font-semibold leading-5 truncate w-full">
                        {{ currentChannel.channel_name }}
                    </div>
                    <div class="text-xs font-light leading-4 truncate w-full">
                        @{{ currentChannel.account_name }}
                    </div>
                </div>

                <div
                    class="w-5 p-[6px] shrink-0 overflow-hidden flex-col justify-center items-center gap-[2px] inline-flex"
                >
                    <div class="w-[2px] h-[2px] bg-gray-400 rounded-full"></div>
                    <div class="w-[2px] h-[2px] bg-gray-400 rounded-full"></div>
                    <div class="w-[2px] h-[2px] bg-gray-400 rounded-full"></div>
                </div>
            </div>

            <div
                v-if="showListAll && (props.channels.length > 0)"
                :class="[
                    'absolute rounded shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] w-[210px] top-16 overflow-hidden',
                ]"
            >
                <ExpandedChannelList
                    :channels="props.channels"
                    :current-channel="currentChannel"
                    @channel-selected="changeChannel"
                    @close-channel-list="showListAll = false"
                    @create-channel="redirectToChannelCreation()"
                />
            </div>
        </div>
    </template>
</template>

<script setup>
import {
    ref,
    defineProps,
    computed,
    onMounted,
    onBeforeUnmount,
} from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import axios from "axios";
import ExpandedChannelList from "./ExpandedChannelList.vue";

const props = defineProps({
    isMobile: {
        type: Boolean,
        default: false,
    },
    channels: {
        type: Array,
        default: () => [],
    },
});

const store = useStore();
const router = useRouter();

// 開閉状態
const showListAll = ref(false);

// 外クリック判定用の参照（PC と SP で分離）
const menuRef = ref(null); // PC側ラッパーに ref="menuRef"
const mobileMenuRef = ref(null); // SP側ラッパーに ref="mobileMenuRef"

const accessToken = store.state.accessToken;
const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;

const currentChannel = computed(() => {
    const ch = props.channels.find((ch) => ch.is_current === true);
    return ch || {};
});

const redirectToChannelCreation = () => {
    showListAll.value = false;
    router.push({ name: "ChannelCreation" });
};

const changeChannel = async (channel_id) => {
    try {
        const response = await axios.post(
            `${apiEndpoint}/channels/${channel_id}/current/`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );
        if (response.status === 200) {
            window.location.reload();
        } else {
            console.error("チャンネルの変更に失敗しました。", response.data);
        }
    } catch (error) {
        console.error("チャンネルの変更に失敗しました。", error);
    }
};

// メニュー範囲外クリックで閉じる
const handleClickOutside = (e) => {
    if (!showListAll.value) return;

    const clickedInsidePC = menuRef.value?.contains(e.target);
    const clickedInsideSP = mobileMenuRef.value?.contains(e.target);

    if (!clickedInsidePC && !clickedInsideSP) {
        showListAll.value = false;
    }
};

onMounted(() => {
    document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
});
</script>
